/**
 * @module  public/js/services/taskreplyservice.js
 * @class   TaskReplyService
 * @summary Fetch reply info for a task from Firestore.
 */

import {
  where,
  orderBy,
  limit,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';

export default class TaskReplyService {
  #fs;
  constructor({ firestoreService }) {
    this.#fs = firestoreService;
  }

  async get(taskId) {
    if (!taskId) return {};
    return (await this.#fs.getDocument(`tasks/${taskId}`)) || {};
  }

  /**
   * Look for replies in earlier steps of the same sequence.
   *
   * @param {string} sequenceId
   * @param {number} stepIndex
   * @returns {Promise<{replyReceived?:boolean,replySnippet?:string,replyTaskId?:string}>}
   */
  async getSequenceReply(sequenceId, stepIndex) {
    if (!sequenceId || stepIndex == null) return {};

    const runQuery = async (idField, idxField) => {
      const constraints = [
        where(idField, '==', sequenceId),
        where(idxField, '<', stepIndex),
        where('replyReceived', '==', true),
        orderBy(idxField, 'desc'),
        limit(10),
      ];
      try {
        return await this.#fs.query('tasks', constraints);
      } catch (err) {
        console.warn('[TaskReplyService] query failed', err);
        return [];
      }
    };

    // First try new-style fields (`sequenceId`/`stepIndex`), then fall back
    // to legacy (`enrolId`/`stepIdx`).
    let rows = await runQuery('sequenceId', 'stepIndex');
    if (!rows.length) {
      rows = await runQuery('enrolId', 'stepIdx');
    }

    const firstUnacknowledged = rows.find(
      (row) => row && row.replyAcknowledged !== true
    );
    if (firstUnacknowledged) {
      return {
        replyReceived: true,
        replySnippet: firstUnacknowledged.replySnippet || '',
        replyTaskId: firstUnacknowledged.id || '',
      };
    }
    return { replyReceived: false };
  }

  async acknowledge(taskId) {
    if (!taskId) return;
    await this.#fs.updateDocument(`tasks/${taskId}`, {
      replyAcknowledged: true,
    });
  }
}
